/**
 * MobileSourceReader — a mobile reader for a captured source/transcript, with the vocal-emotion
 * timeline overlay folded in (CAP-2.3, Phase 2).
 *
 * Route: /partner/m/source/:id
 *
 * READ-ONLY and offline-aware, following the same substrate as MobilePortfolioGlance / MobileInbox /
 * MobileMeetingPrep:
 *   • ONE useCachedQuery resolves the whole envelope (source header + transcript utterances + emotion
 *     rows) and write-throughs it as a unit under cacheKey "mobile.source.<id>".
 *   • The queryFn THROWS on { error } so the cached last-known-good keeps serving offline instead of
 *     being overwritten with a partial/empty value.
 *   • A stable EMPTY envelope guarantees the screen is never blank (offline cold-start still renders a
 *     coherent shell).
 *   • The "Saved <relative time>" header hint + amber-when-offline colour mirror the sibling pages.
 *
 * TRANSCRIPT SOURCING — handles both the documented future shape and the live reality:
 *   1. `raw_metadata.deepgram.utterances` — array of { start(sec), end(sec), speaker, transcript|text }.
 *      This is the spec'd shape; we use it verbatim when present.
 *   2. FALLBACK (current live data): the utterances array is not yet stored; `extracted_text` is a
 *      formatted transcript whose lines look like "[1.4s | Speaker 0] <text>". We parse those into
 *      utterances so the emotion overlay can still align by time.
 *   3. LAST RESORT: no parseable structure → render `extracted_text` as a single plain block (no
 *      timing, so no overlay).
 *
 * EMOTION OVERLAY: research_source_emotion rows for this source. RLS only returns rows to a
 * read-capable adviser when the source carries a live consent row with emotion_inference_ok=true; a
 * non-consented / non-entitled reader simply gets [] → EmotionTimeline renders nothing. We do NOT pass
 * a per-speaker role map (the voiceprint speaker-role table isn't populated yet — see EmotionTimeline
 * for the honest client-suppression behaviour), so the overlay is adviser-gated as a whole.
 *
 * The Supabase generated types are stale for these tables, so `.from(...)` is cast through `any`
 * (matching the sibling pages). No writes. Web/desktop are untouched (this is a /partner/m/* surface).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ChevronLeft, FileText, Search, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { cn } from '@/lib/utils';
import {
  EmotionTimeline,
  type Utterance,
  type EmotionSegment,
  type EmotionScore,
} from '@/components/mobile/EmotionTimeline';

// ---------------------------------------------------------------------------
// Envelope shapes (cached as a unit)
// ---------------------------------------------------------------------------

interface SourceReaderData {
  id: string;
  title: string | null;
  createdAt: string | null;
  /** Time-aligned utterances (from deepgram.utterances or parsed from extracted_text). */
  utterances: Utterance[];
  /** Full transcript text — the last-resort render when no utterances could be derived. */
  plainText: string | null;
  /** research_source_emotion rows, normalised (top-first emotions). */
  emotions: EmotionSegment[];
}

function emptyEnvelope(id: string): SourceReaderData {
  return { id, title: null, createdAt: null, utterances: [], plainText: null, emotions: [] };
}

// ---------------------------------------------------------------------------
// Parsing helpers (defensive — never throw; bad shapes degrade to fewer utterances / plain text)
// ---------------------------------------------------------------------------

function asNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

// Shape 1: raw_metadata.deepgram.utterances — [{ start(sec), end(sec), speaker, transcript|text }].
function utterancesFromDeepgram(raw: unknown): Utterance[] {
  const dg = (raw as { deepgram?: { utterances?: unknown } } | null)?.deepgram;
  const arr = dg?.utterances;
  if (!Array.isArray(arr)) return [];
  const out: Utterance[] = [];
  for (const item of arr) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const start = asNumber(o.start);
    const end = asNumber(o.end);
    const text = (typeof o.transcript === 'string' && o.transcript) ||
      (typeof o.text === 'string' && o.text) || '';
    if (start == null || end == null || !text) continue;
    const speakerRaw = o.speaker;
    const speaker: number | string | null =
      typeof speakerRaw === 'number' || typeof speakerRaw === 'string' ? speakerRaw : null;
    out.push({ start, end, speaker, text });
  }
  return out;
}

// Shape 2 (live fallback): parse "[1.4s | Speaker 0] <text>" lines out of extracted_text. The `end`
// of each line is approximated by the next line's start (or +4s for the last), which is enough for
// the emotion overlay to align by overlap. Lines that don't match are skipped (the plain-text render
// covers the unstructured case).
const LINE_RE = /^\[\s*([\d.]+)s\s*\|\s*Speaker\s+(\d+)\s*\]\s*(.*)$/i;

function utterancesFromExtractedText(text: string | null): Utterance[] {
  if (!text) return [];
  const parsed: Array<{ start: number; speaker: number; text: string }> = [];
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    const m = LINE_RE.exec(line);
    if (!m) continue;
    const start = Number(m[1]);
    const speaker = Number(m[2]);
    if (!Number.isFinite(start) || !Number.isFinite(speaker)) continue;
    parsed.push({ start, speaker, text: m[3] ?? '' });
  }
  if (parsed.length === 0) return [];
  return parsed.map((p, i) => {
    const next = parsed[i + 1];
    // Approximate the line's end as the next line's start. Guard against a zero/negative window when
    // two consecutive lines share a timestamp (or are out of order) by enforcing a 0.5s minimum, so
    // back-to-back same-timestamp utterances still present a non-empty window for emotion overlap.
    const end = next ? Math.max(p.start + 0.5, next.start) : p.start + 4;
    return { start: p.start, end, speaker: p.speaker, text: p.text };
  });
}

// Normalise a research_source_emotion row. `emotions` is an array of { name, score }, top-first; we
// keep it as-is but defensively coerce types and drop malformed entries.
function normaliseEmotionRow(row: Record<string, unknown>): EmotionSegment | null {
  const startMs = asNumber(row.start_ms);
  const endMs = asNumber(row.end_ms);
  if (startMs == null || endMs == null) return null;
  const rawEmotions = row.emotions;
  const scores: EmotionScore[] = [];
  if (Array.isArray(rawEmotions)) {
    for (const e of rawEmotions) {
      if (!e || typeof e !== 'object') continue;
      const o = e as Record<string, unknown>;
      const name = typeof o.name === 'string' ? o.name : null;
      const score = asNumber(o.score);
      if (!name || score == null) continue;
      scores.push({ name, score });
    }
  }
  if (scores.length === 0) return null;
  const speaker = typeof row.speaker === 'string' ? row.speaker : null;
  return { speaker, startMs, endMs, emotions: scores };
}

// ---------------------------------------------------------------------------
// Fetch (one envelope; THROWS on error so the cached placeholder serves offline)
// ---------------------------------------------------------------------------

async function fetchSource(id: string): Promise<SourceReaderData> {
  // Source header + body. RLS gates SELECT to read-capable users; a non-entitled reader gets no row
  // (handled as an empty-but-coherent shell, never an error banner).
  const srcRes = await (supabase.from('research_sources') as any)
    .select('id, title, created_at, extracted_text, raw_metadata')
    .eq('id', id)
    .maybeSingle();
  if (srcRes.error) throw srcRes.error;

  const src = srcRes.data as
    | {
        id: string;
        title: string | null;
        created_at: string | null;
        extracted_text: string | null;
        raw_metadata: unknown;
      }
    | null;

  if (!src) {
    // No accessible row — a valid quiet empty shell.
    return emptyEnvelope(id);
  }

  // Utterances: prefer the documented deepgram array; else parse the live extracted_text format.
  let utterances = utterancesFromDeepgram(src.raw_metadata);
  if (utterances.length === 0) {
    utterances = utterancesFromExtractedText(src.extracted_text);
  }

  // Emotion rows. RLS only returns rows when the source has a live consent row with
  // emotion_inference_ok=true AND the reader is read-capable; otherwise [] (→ overlay renders nothing).
  // `research_source_emotion` is newer than the generated types, so the table literal isn't in the
  // `.from()` overload union — cast `supabase` to `any` first (the orchestrator regenerates types).
  const emoRes = await (supabase as any)
    .from('research_source_emotion')
    .select('id, speaker, start_ms, end_ms, emotions, model, created_at')
    .eq('source_id', id)
    .order('start_ms', { ascending: true });
  if (emoRes.error) throw emoRes.error;

  const emotions = (Array.isArray(emoRes.data) ? emoRes.data : [])
    .map((r: Record<string, unknown>) => normaliseEmotionRow(r))
    .filter((e: EmotionSegment | null): e is EmotionSegment => e != null);

  return {
    id: src.id,
    title: src.title,
    createdAt: src.created_at,
    utterances,
    // Keep the raw text as the last-resort render when we couldn't derive any utterances.
    plainText: utterances.length === 0 ? src.extracted_text : null,
    emotions,
  };
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function mmss(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatCreatedAt(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString([], {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

// Escape a user query for safe use inside a RegExp (so search treats it as a literal substring).
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Render `text` with every case-insensitive occurrence of `query` wrapped in a highlight <mark>. Returns
// the plain string when there's no query/match, so non-matching rows are untouched.
function highlight(text: string, query: string): ReactNode {
  const q = query.trim();
  if (!q) return text;
  // Split on the captured group so the matched substrings are preserved as alternating array elements.
  const re = new RegExp(`(${escapeRegExp(q)})`, 'gi');
  const parts = text.split(re);
  if (parts.length === 1) return text;
  const ql = q.toLowerCase();
  return parts.map((part, i) =>
    // The captured (matched) segments are exactly those equal to the query, case-insensitively.
    part.toLowerCase() === ql ? (
      <mark key={i} className="rounded-sm bg-primary/25 px-0.5 text-foreground">
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MobileSourceReader() {
  const online = useIsOnline();
  const { id = '' } = useParams<{ id: string }>();

  const source = useCachedQuery<SourceReaderData>({
    queryKey: ['mobile-source', id],
    cacheKey: `mobile.source.${id}`,
    queryFn: () => fetchSource(id),
    enabled: id.length > 0,
  });

  // Never blank: fresh/placeholder, else the empty (but coherent) envelope.
  const data = source.data ?? emptyEnvelope(id);

  // ---- Local in-page transcript search (no network) ------------------------------------------------
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim();
  // Refs to each utterance <li> so we can scroll to the first match.
  const utteranceRefs = useRef<Array<HTMLLIElement | null>>([]);

  // Indices of utterances whose text contains the query (case-insensitive). Empty when no query.
  const matchIndices = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (!q) return [] as number[];
    const out: number[] = [];
    data.utterances.forEach((u, i) => {
      if (u.text.toLowerCase().includes(q)) out.push(i);
    });
    return out;
  }, [trimmedQuery, data.utterances]);

  // Scroll the first match into view whenever the query (or the result set) changes.
  useEffect(() => {
    if (matchIndices.length === 0) return;
    const el = utteranceRefs.current[matchIndices[0]];
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [matchIndices]);

  const matchSet = useMemo(() => new Set(matchIndices), [matchIndices]);

  // "Saved <relative time>" affordance when serving last-known-good (offline or pre-fetch).
  const showStale = source.isFromCache && source.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && source.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(source.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  const dateLabel = useMemo(() => formatCreatedAt(data.createdAt), [data.createdAt]);

  // Hard error only when we truly have nothing to show (no cache to fall back to).
  const hardError = source.isError && !source.isFromCache && source.data === undefined;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/inbox"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Inbox
        </Link>
        <p className="text-sm text-muted-foreground">Source transcript</p>
        <h1 className="font-heading text-2xl font-semibold text-primary">
          {data.title ?? 'Untitled source'}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {dateLabel && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              {dateLabel}
            </p>
          )}
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      <div className="space-y-3">
        {hardError ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              Couldn’t load
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {online
                ? 'We couldn’t load this transcript just now. Pull back in a moment.'
                : 'You’re offline and there’s no saved copy of this transcript yet. It’ll load once you reconnect.'}
            </p>
          </section>
        ) : (
          <>
            {/* Emotion overlay — folded in above the transcript. Renders nothing when there are no
                emotion rows (non-consented / non-entitled), so this is invisible in that case. */}
            <EmotionTimeline utterances={data.utterances} emotions={data.emotions} />

            {/* Transcript */}
            {data.utterances.length > 0 ? (
              <section className="rounded-xl border border-border bg-card p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h2 className="font-heading text-sm font-semibold">Transcript</h2>
                  {trimmedQuery && (
                    <span className="shrink-0 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                      {matchIndices.length} {matchIndices.length === 1 ? 'match' : 'matches'}
                    </span>
                  )}
                </div>

                {/* Local in-page search over utterances[].text — highlight + scroll to first match. */}
                <div className="relative mb-3">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    inputMode="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search transcript"
                    aria-label="Search transcript"
                    className="h-10 w-full rounded-lg border border-border bg-background pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground/70 active:bg-muted/50"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>

                {trimmedQuery && matchIndices.length === 0 && (
                  <p className="mb-3 text-sm text-muted-foreground">No matches in this transcript.</p>
                )}

                <ul className="space-y-3">
                  {data.utterances.map((u, i) => {
                    const isMatch = matchSet.has(i);
                    return (
                      <li
                        key={i}
                        ref={(el) => {
                          utteranceRefs.current[i] = el;
                        }}
                        className={cn(
                          'text-sm leading-snug',
                          isMatch && 'rounded-lg bg-muted/40 px-2 py-1.5',
                        )}
                      >
                        <p className="mb-0.5 flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                          {u.speaker != null && <span>Speaker {String(u.speaker)}</span>}
                          <span className="tabular-nums">{mmss(u.start)}</span>
                        </p>
                        <p>{trimmedQuery ? highlight(u.text, trimmedQuery) : u.text}</p>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : data.plainText && data.plainText.trim().length > 0 ? (
              <section className="rounded-xl border border-border bg-card p-4">
                <h2 className="mb-3 font-heading text-sm font-semibold">Transcript</h2>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                  {data.plainText}
                </p>
              </section>
            ) : (
              <section className="rounded-xl border border-dashed border-border bg-card/40 p-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground/60" aria-hidden="true" />
                  <h2 className="font-heading text-sm font-semibold">No transcript yet</h2>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {source.isLoading
                    ? 'Loading transcript…'
                    : !online
                      ? 'You’re offline — this will fill in once you reconnect.'
                      : 'This source has no transcript text available.'}
                </p>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
